import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { AllRawData } from '../../raw-types.ts';

// Server-side degen league (norns/jotnar/ragnarok) in-game data — was
// missing from phase 1 build entirely, so degen sessions had no
// server-validated wave/enemy data. degen_season tells you what's running;
// these rows tell you how each league plays.
export const DegenLeagueSchema = defineSchema({
  name: 'serverDegenLeagues',
  fields: [
    ['id', 'number'],
    ['waves', { kind: 'array', of: 'stageWave' }],
  ],
  lookupKeys: ['id'],
});

export interface RawDegenLeagueRow {
  id: number;
}

export function degenLeagueFromRaw(
  row: RawDegenLeagueRow,
  raw: Pick<AllRawData, 'degen_league_wave' | 'degen_league_wave_enemy'>
) {
  const leagueWaves = (raw.degen_league_wave ?? [])
    .filter((w) => w.degen_league_id === row.id)
    .sort((a, b) => a.wave_number - b.wave_number);
  const lastWaveNumber =
    leagueWaves.length > 0 ? leagueWaves[leagueWaves.length - 1].wave_number : -1;

  const waves = leagueWaves.map((w) => {
    const isLastWave = w.wave_number === lastWaveNumber;
    const shopConfigId = !isLastWave && w.shop_config_id != null ? w.shop_config_id : null;

    const enemies = (raw.degen_league_wave_enemy ?? [])
      .filter((e) => e.degen_league_id === row.id && e.wave_number === w.wave_number)
      .map((e) => ({
        id: e.enemy_id,
        count: 1,
        // Range columns (min/max). Worker uses the upper bound for spawn
        // bottleneck math so the player is given the benefit of the doubt.
        spawnInterval: e.min_spawn_interval ?? e.max_spawn_interval,
        spawnPattern: e.spawn_pattern ?? null,
        healthMultiplier: e.max_health_multiplier,
        speedMultiplier: e.max_speed_multiplier,
        atkMultiplier: e.max_atk_multiplier,
      }));

    return {
      waveNumber: w.wave_number,
      killGoal: w.kill_goal,
      timeLimitSeconds: w.time_limit_seconds,
      silverReward: w.silver_reward,
      shopConfigId,
      enemies,
    };
  });

  return { id: row.id, waves };
}

registerCategorySchema('serverDegenLeagues', DegenLeagueSchema);
